import React, { useMemo } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag";

const PHONE_COUNTRIES = [
  { dial: "+49", iso: "DE" },
  { dial: "+41", iso: "CH" },
  { dial: "+43", iso: "AT" },
  { dial: "+34", iso: "ES" },
  { dial: "+39", iso: "IT" },
] as const;

function splitPhone(phone: string | null | undefined): {
  prefix: string;
  national: string;
} {
  const raw = phone?.trim() ?? "";
  if (!raw) return { prefix: "+49", national: "" };
  const withPlus = raw.match(/^(\+\d+)\s*(.*)$/);
  if (withPlus) return { prefix: withPlus[1], national: withPlus[2] ?? "" };
  return { prefix: "+49", national: raw };
}

function combinePhone(prefix: string, national: string): string {
  const n = national.trim();
  return n ? `${prefix} ${n}` : prefix;
}

export interface PhoneNumberInputProps {
  /** Full number including country prefix, e.g. `+49 170 1234567` */
  value: string;
  /** Called with the updated full number whenever prefix or national part changes */
  onChange: (phone: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

export default function PhoneNumberInput({
  value,
  onChange,
  error,
  helperText,
  required,
}: PhoneNumberInputProps) {
  const { t } = useTranslation();
  const phoneParts = useMemo(() => splitPhone(value), [value]);

  const handlePhoneCountryChange = (nextPrefix: string) => {
    onChange(combinePhone(nextPrefix, splitPhone(value).national));
  };

  const handlePhoneNationalChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange(combinePhone(splitPhone(value).prefix, event.target.value));
  };

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", flexShrink: 0 }}>
        <Select
          variant="outlined"
          value={phoneParts.prefix}
          onChange={(event) => handlePhoneCountryChange(event.target.value)}
          sx={{
            minWidth: 72,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            height: 56,
          }}
        >
          {PHONE_COUNTRIES.map((country) => (
            <MenuItem key={country.dial} value={country.dial}>
              <ReactCountryFlag
                countryCode={country.iso}
                svg
                style={{ fontSize: "1.5em", lineHeight: "1.5em" }}
              />
            </MenuItem>
          ))}
        </Select>
      </Box>
      <TextField
        fullWidth
        required={required}
        label={t("Phone")}
        value={phoneParts.national}
        onChange={handlePhoneNationalChange}
        error={error}
        helperText={helperText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <Typography
                component="span"
                variant="body1"
                sx={{ color: "text.secondary" }}
              >
                {phoneParts.prefix}
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          ml: "-1px",
          flex: 1,
          minWidth: 0,
          "& .MuiOutlinedInput-root": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
          height: 56,
        }}
      />
    </Box>
  );
}
